import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect } from 'react';
import { AudienceResponseDTO } from 'src/modules/audiences/infra/dtos';
import { isValidSlug, slugify } from 'src/modules/tiers/domain/slug';
import { Tier, TierFormValues } from 'src/modules/tiers/domain/types';
import * as Yup from 'yup';

import { FormikInput, FormikTextArea } from '@/components/forms';
import { Button, Text } from '@/design-system/components/ui';

import AudienceSelectField from './AudienceSelectField';

const tierValidationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .max(100, 'Name must be 100 characters or less')
    .trim()
    .test('valid-slug', 'Name must produce a valid slug (letters, numbers, hyphens only)', (value) => {
      if (!value) return true;
      const slug = slugify(value.trim());
      return isValidSlug(slug);
    }),
  description: Yup.string().max(500, 'Description must be 500 characters or less'),
  rank: Yup.number()
    .required('Rank is required')
    .integer('Rank must be a whole number')
    .min(1, 'Rank must be at least 1'),
  audienceId: Yup.string().nullable(),
});

interface TierFormProps {
  editingTier: Tier | null;
  audiences: AudienceResponseDTO[];
  defaultRank: number;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (values: TierFormValues) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export default function TierForm({
  editingTier,
  audiences,
  defaultRank,
  isPending,
  onCancel,
  onSubmit,
  onDirtyChange,
}: TierFormProps) {
  const isEdit = editingTier !== null;

  const formik = useFormik<TierFormValues>({
    initialValues: {
      name: editingTier?.name ?? '',
      description: editingTier?.description ?? '',
      rank: editingTier?.rank ?? defaultRank,
      audienceId: editingTier?.audienceId ?? null,
    },
    validationSchema: tierValidationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  const { handleSubmit, dirty } = formik;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit}>
        <div className="tw:flex tw:flex-col tw:gap-6">
          <FormikInput label="Name" name="name" required disabled={isPending} placeholder="e.g. Gold" />

          <FormikTextArea
            label="Description"
            name="description"
            disabled={isPending}
            placeholder="Optional description for this tier"
            helperText="Maximum 500 characters"
            maxLength={500}
            rows={3}
          />

          <div className="tw:flex tw:flex-col tw:gap-1">
            <FormikInput label="Rank" name="rank" type="number" required disabled={isPending} placeholder="1" />
            <Text variant="body-sm" color="muted">
              Rank 1 is the highest priority. When a user qualifies for multiple tiers, the highest rank wins.
            </Text>
          </div>

          <AudienceSelectField audiences={audiences} disabled={isPending} />

          <div className="tw:flex tw:flex-row tw:justify-end tw:gap-4">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending} type="button">
              Cancel
            </Button>
            <Button size="sm" type="submit" variant="primary" disabled={isPending} isLoading={isPending}>
              {isEdit ? 'Save Changes' : 'Create Tier'}
            </Button>
          </div>
        </div>
      </Form>
    </FormikProvider>
  );
}
